import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const THRESHOLD = 80;
const MAX_PULL = 120;

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

// Znajduje najbliższy przewijalny kontener (np. lista wiadomości), w którym
// zaczął się dotyk. Jeśli taki kontener da się jeszcze przewinąć w górę,
// gest ma przewijać jego zawartość, a nie odświeżać stronę.
function findScrollableAncestor(el: EventTarget | null): HTMLElement | null {
  let node = el as HTMLElement | null;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Własny pull-to-refresh dla trybu standalone (PWA na ekranie głównym),
 * gdzie iOS wyłącza natywne odświeżanie. Działa tylko gdy strona jest
 * przewinięta na samą górę; po przeciągnięciu w dół ponad próg strona
 * się przeładowuje. Aby cofnąć: usuń <PullToRefresh /> z App.tsx i ten plik.
 */
export default function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);
  const scrollContainer = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isStandalone()) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      // Gest wewnątrz okna, które samo obsługuje przeciąganie (np. powiększenie
      // zdjęcia zamykane ściągnięciem w dół), nie może odświeżać strony.
      if ((e.target as HTMLElement | null)?.closest?.("[data-no-pull-refresh]")) {
        active.current = false;
        startY.current = null;
        return;
      }
      scrollContainer.current = findScrollableAncestor(e.target);
      // Jeśli dotyk zaczął się na przewijalnym kontenerze, który nie jest
      // na samej górze (np. starsza część rozmowy), nie włączamy odświeżania.
      const containerScrollable =
        scrollContainer.current !== null && scrollContainer.current.scrollTop > 0;
      if (window.scrollY <= 0 && e.touches.length === 1 && !containerScrollable) {
        startY.current = e.touches[0].clientY;
        active.current = true;
      } else {
        active.current = false;
        startY.current = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active.current || startY.current === null || refreshing) return;
      // Jeśli użytkownik zdążył przewinąć stronę, przestajemy śledzić gest.
      if (window.scrollY > 0) {
        active.current = false;
        setPull(0);
        return;
      }
      // Jeśli w trakcie gestu kontener pod palcem się przewinął,
      // to było przewijanie treści, nie odświeżanie.
      if (scrollContainer.current && scrollContainer.current.scrollTop > 0) {
        active.current = false;
        setPull(0);
        return;
      }
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0) {
        setPull(0);
        return;
      }
      // Tłumienie, żeby gest nie był zbyt "gumowy".
      setPull(Math.min(MAX_PULL, delta * 0.5));
    };

    const onTouchEnd = () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      setPull((current) => {
        if (current >= THRESHOLD * 0.5 && !refreshing) {
          setRefreshing(true);
          // Krótka pauza, żeby spinner był widoczny zanim strona zniknie.
          setTimeout(() => window.location.reload(), 250);
          return THRESHOLD * 0.5;
        }
        return 0;
      });
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [refreshing]);

  if (pull <= 0 && !refreshing) return null;

  const visible = refreshing ? THRESHOLD * 0.5 : pull;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center"
      style={{
        transform: `translateY(${visible - 40}px)`,
        transition: active.current ? "none" : "transform 200ms ease-out",
      }}
    >
      <div className="mt-2 flex h-9 w-9 items-center justify-center rounded-full bg-background shadow-md border">
        <Loader2
          className="h-5 w-5 text-primary"
          style={{
            animation: refreshing ? "spin 1s linear infinite" : undefined,
            transform: refreshing ? undefined : `rotate(${visible * 3}deg)`,
          }}
        />
      </div>
    </div>
  );
}
